import { Button, PostHogUserButton } from "@dash/form";
import { HeaderBar, Page, PageHeader } from "@dash/page";
import { Pagination } from "@dash/search";
import { QuerySkeleton } from "@dash/skeleton";
import { useTheme } from "@emotion/react";
import { formatCurrency } from "@legacy-megarax/react-utils";
import { formatDistanceToNowStrict } from "date-fns";
import { pl } from "date-fns/locale";
import { useMemo, useState } from "react";
import { MdChevronRight, MdOutlinePersonSearch, MdShoppingCart } from "react-icons/md";
import { Link, useNavigate, useParams } from "react-router-dom-v5-compat";

import { Order } from "@megaron/fulfillment-contracts";
import { useClientManager } from "@megaron/react-clients";
import { Uuid } from "@megaron/uuid";

import { AccountHistoryTable } from "./AccountHistoryTable";
import { getMegaraxPath } from "@megaron/megarax-uri";

const OrderItem: React.FC<{ order: Order }> = ({ order }) => {
  const theme = useTheme();
  const [isOpen, setIsOpen] = useState(false);

  return (
    <div
      css={{
        display: "flex",
        flexDirection: "column",
        border: `1px solid ${theme.colors.border}`,
        borderRadius: theme.mediumBorderRadius,
        overflow: "hidden",
        backgroundColor: theme.colors.background,
      }}
    >
      <button
        onClick={() => setIsOpen(!isOpen)}
        css={{
          width: "100%",
          display: "flex",
          alignItems: "center",
          gap: "1rem",
          justifyContent: "space-between",
          padding: "1rem",
          backgroundColor: isOpen ? theme.colors.background : "transparent",
          cursor: "pointer",
          transition: "background-color 0.2s ease",
          border: "none",
          textAlign: "left",
          "&:hover": {
            backgroundColor: theme.colors.background,
          },
        }}
      >
        <div css={{ display: "flex", flexDirection: "column", flex: 1, gap: "0.25rem" }}>
          <Link
            to={getMegaraxPath.uri("megarax://fulfillment/orders/id/{id}", { id: order.id })}
            onClick={(e) => e.stopPropagation()}
            css={{
              cursor: "pointer",
              color: theme.colors.primary,
              fontWeight: 600,
              fontSize: "0.9375rem",
              textDecoration: "none",
              "&:hover": {
                textDecoration: "underline",
              },
            }}
          >
            WEB-{order.orderNumber}
          </Link>
          <span css={{ color: theme.colors.secondaryText || "#666", fontSize: "0.8125rem" }}>
            {formatDistanceToNowStrict(order.processedAt, { locale: pl, addSuffix: true })}
          </span>
        </div>
        <div css={{ display: "flex", alignItems: "center", gap: "1rem" }}>
          <span css={{ fontWeight: 600, fontSize: "0.9375rem", color: theme.colors.default }}>
            {formatCurrency(order.totalPrice, order.currency)}
          </span>
          <MdChevronRight
            size={24}
            css={{
              color: theme.colors.secondaryText || "#666",
              flexShrink: 0,
              transform: isOpen ? "rotate(-90deg)" : "rotate(90deg)",
              transition: "transform 0.2s ease-in-out",
            }}
          />
        </div>
      </button>
      {isOpen && (
        <div
          css={{
            padding: "1rem",
            borderTop: `1px solid ${theme.colors.border}`,
            backgroundColor: theme.colors.background,
            animation: "slideDown 0.2s ease-out",
            "@keyframes slideDown": {
              from: {
                opacity: 0,
                maxHeight: 0,
              },
              to: {
                opacity: 1,
                maxHeight: "1000px",
              },
            },
          }}
        >
          <div css={{ display: "flex", flexDirection: "column", gap: "0.75rem" }}>
            {order.items && order.items.length > 0 ? (
              <div css={{ display: "flex", flexDirection: "column", gap: "0.5rem" }}>
                {order.items.map((item, idx) => (
                  <div
                    key={idx}
                    css={{
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "flex-start",
                      padding: "0.75rem",
                      backgroundColor: theme.colors.selectedBackground || theme.colors.background,
                      borderRadius: theme.smallBorderRadius || "4px",
                      fontSize: "0.875rem",
                      gap: "1rem",
                      transition: "background-color 0.2s ease",
                      "&:hover": {
                        backgroundColor:
                          theme.colors.hover || theme.colors.selectedBackground || theme.colors.background,
                      },
                    }}
                  >
                    <span css={{ flex: 1, lineHeight: 1.5 }}>{item.name}</span>
                    <div css={{ display: "flex", alignItems: "center", gap: "0.75rem", flexShrink: 0 }}>
                      <span css={{ color: theme.colors.secondaryText || "#666", fontSize: "0.8125rem" }}>
                        x{item.quantity}
                      </span>
                      <span css={{ fontWeight: 600, color: theme.colors.default }}>
                        {formatCurrency(item.price, order.currency)}
                      </span>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <span css={{ color: theme.colors.secondaryText || "#666", fontSize: "0.875rem", fontStyle: "italic" }}>
                Brak produktów
              </span>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export const AccountPage = () => {
  const theme = useTheme();
  const { userId } = useParams<{ userId: string }>();
  const navigate = useNavigate();
  const [ordersPage, setOrdersPage] = useState(0);
  const ordersPageSize = 5;

  const cashRewardsClient = useClientManager("cashRewards");
  const crmClient = useClientManager("crm");
  const fulfillmentClient = useClientManager("fulfillment");
  const loyaltyPointsClient = useClientManager("loyaltyPoints");

  const accountQuery = cashRewardsClient.accountQuery().useQuery(
    {
      userId: userId!,
    },
    { enabled: !!userId },
  );

  const pointsAccountQuery = loyaltyPointsClient.account().useQuery({ uuid: userId as Uuid }, { enabled: !!userId });

  const customerQuery = crmClient.getCustomer().useQuery({ customerId: userId as Uuid }, { enabled: !!userId });

  const ordersQuery = fulfillmentClient.ordersQuery().useQuery(
    {
      userId: userId!,
      limit: ordersPageSize,
      offset: ordersPage * ordersPageSize,
    },
    { enabled: !!userId },
  );

  const cartsQuery = fulfillmentClient.cartsQuery().useQuery(
    {
      limit: 100,
      isArchived: false,
    },
    { enabled: !!userId },
  );

  const userCart = useMemo(() => {
    if (!userId || !cartsQuery.data) return null;
    return cartsQuery.data.items.find((cart) => cart.userId === userId && !cart.isArchived) || null;
  }, [userId, cartsQuery.data]);

  if (!userId) {
    return (
      <Page css={{ display: "flex", flexDirection: "column", gap: "1rem", maxWidth: "100%" }}>
        <PageHeader>Błąd</PageHeader>
        <div css={{ color: theme.colors.danger, padding: "1rem" }}>Nie podano identyfikatora użytkownika.</div>
      </Page>
    );
  }

  return (
    <QuerySkeleton query={accountQuery}>
      {(account) => {
        const customer = customerQuery.data;

        return (
          <>
            <HeaderBar header={account.userId} />

            <Page css={{ display: "flex", flexDirection: "column", gap: "1rem" }}>
              <div
                css={{
                  display: "flex",
                  flexDirection: "column",
                  gap: "1.5rem",
                  padding: "1rem",
                  marginTop: "3rem",
                }}
              >
                {customer && (
                  <div
                    css={{
                      display: "flex",
                      flexDirection: "column",
                      gap: "1rem",
                      background: theme.colors.background,
                      borderRadius: theme.mediumBorderRadius,
                    }}
                  >
                    <div css={{ fontSize: "1rem", fontWeight: 600, marginBottom: "0.25rem" }}>
                      Informacje o użytkowniku
                    </div>
                    <div
                      css={{
                        display: "grid",
                        gridTemplateColumns: "repeat(auto-fit, minmax(280px, 1fr))",
                        gap: "1rem",
                      }}
                    >
                      {customer.firstName || customer.lastName ? (
                        <div css={{ display: "flex", flexDirection: "column", gap: "0.25rem" }}>
                          <div css={{ fontWeight: 600, fontSize: "0.875rem", color: theme.colors.secondaryText }}>
                            Imię i nazwisko
                          </div>
                          <div>{[customer.firstName, customer.lastName].filter(Boolean).join(" ") || "—"}</div>
                        </div>
                      ) : null}
                      {customer.email ? (
                        <div css={{ display: "flex", flexDirection: "column", gap: "0.25rem" }}>
                          <div css={{ fontWeight: 600, fontSize: "0.875rem", color: theme.colors.secondaryText }}>
                            Email
                          </div>
                          <div>{customer.email}</div>
                        </div>
                      ) : null}
                      {customer.phoneNumber ? (
                        <div css={{ display: "flex", flexDirection: "column", gap: "0.25rem" }}>
                          <div css={{ fontWeight: 600, fontSize: "0.875rem", color: theme.colors.secondaryText }}>
                            Numer telefonu
                          </div>
                          <div>{customer.phoneNumber}</div>
                        </div>
                      ) : null}
                      {customer.address?.postalCode ? (
                        <div css={{ display: "flex", flexDirection: "column", gap: "0.25rem" }}>
                          <div css={{ fontWeight: 600, fontSize: "0.875rem", color: theme.colors.secondaryText }}>
                            Kod pocztowy
                          </div>
                          <div>{customer.address.postalCode}</div>
                        </div>
                      ) : null}
                      {customer.loyaltyJoinedAt ? (
                        <div css={{ display: "flex", flexDirection: "column", gap: "0.25rem" }}>
                          <div css={{ fontWeight: 600, fontSize: "0.875rem", color: theme.colors.secondaryText }}>
                            Członek od
                          </div>
                          <div>{customer.loyaltyJoinedAt.toLocaleString("pl-PL")}</div>
                        </div>
                      ) : null}
                    </div>
                  </div>
                )}
                <QuerySkeleton query={pointsAccountQuery}>
                  {(pointsAccount) => (
                    <div
                      css={{
                        display: "flex",
                        flexDirection: "column",
                        gap: "1rem",
                        background: theme.colors.background,
                        borderRadius: theme.mediumBorderRadius,
                      }}
                    >
                      <div css={{ fontSize: "1rem", fontWeight: 600, marginBottom: "0.25rem" }}>Dane konta</div>
                      <div
                        css={{
                          display: "grid",
                          gridTemplateColumns: "repeat(auto-fit, minmax(280px, 1fr))",
                          gap: "1rem",
                        }}
                      >
                        <div css={{ display: "flex", flexDirection: "column", gap: "0.25rem" }}>
                          <div css={{ fontWeight: 600, fontSize: "0.875rem", color: theme.colors.secondaryText }}>
                            Stan konta
                          </div>
                          <div>{pointsAccount.balance.toFixed(1)} pkt</div>
                        </div>
                        <div css={{ display: "flex", flexDirection: "column", gap: "0.25rem" }}>
                          <div css={{ fontWeight: 600, fontSize: "0.875rem", color: theme.colors.secondaryText }}>
                            Status
                          </div>
                          <div>
                            {pointsAccount.isAffiliate && pointsAccount.isPro
                              ? "Partner, PRO"
                              : pointsAccount.isAffiliate
                                ? "Partner"
                                : pointsAccount.isPro
                                  ? "PRO"
                                  : "—"}
                          </div>
                        </div>
                        {pointsAccount.proNumber !== null && (
                          <div css={{ display: "flex", flexDirection: "column", gap: "0.25rem" }}>
                            <div css={{ fontWeight: 600, fontSize: "0.875rem", color: theme.colors.secondaryText }}>
                              Nr konta PRO
                            </div>
                            <div>{pointsAccount.proNumber}</div>
                          </div>
                        )}
                        <div css={{ display: "flex", flexDirection: "column", gap: "0.25rem" }}>
                          <div css={{ fontWeight: 600, fontSize: "0.875rem", color: theme.colors.secondaryText }}>
                            Liczba skanów
                          </div>
                          <div>{pointsAccount.lifetimeCodeCount}</div>
                        </div>
                      </div>
                    </div>
                  )}
                </QuerySkeleton>
                <div
                  css={{
                    display: "flex",
                    gap: "1rem",
                  }}
                >
                  <div
                    css={{
                      display: "flex",
                      gap: "1rem",
                      alignItems: "center",
                      background: theme.colors.background,
                      borderRadius: theme.mediumBorderRadius,
                    }}
                  >
                    <div css={{ fontWeight: 600 }}>Saldo:</div>
                    <div
                      css={{
                        fontWeight: account.balance && account.balance.greaterThan(0) ? 700 : 400,
                        fontSize: "1.25rem",
                      }}
                    >
                      {account.balance.toFixed(2)} PLN
                    </div>
                  </div>
                  <QuerySkeleton query={pointsAccountQuery}>
                    {(pointsAccount) => (
                      <div
                        css={{
                          display: "flex",
                          gap: "1rem",
                          alignItems: "center",
                          background: theme.colors.background,
                          borderRadius: theme.mediumBorderRadius,
                        }}
                      >
                        <div css={{ fontWeight: 600 }}>Punkty:</div>
                        <div
                          css={{
                            fontWeight: pointsAccount.balance && pointsAccount.balance.greaterThan(0) ? 700 : 400,
                            fontSize: "1.25rem",
                          }}
                        >
                          {pointsAccount.balance.toFixed(1)} pkt
                        </div>
                      </div>
                    )}
                  </QuerySkeleton>
                </div>
                <div
                  css={{
                    display: "flex",
                    gap: "0.75rem",
                    background: theme.colors.background,
                    borderRadius: theme.mediumBorderRadius,
                    flexWrap: "wrap",
                  }}
                >
                  <Button
                    variant="outline"
                    icon={<MdOutlinePersonSearch />}
                    onClick={() => navigate(getMegaraxPath.uri("megarax://crm/customers/id/{id}", { id: Uuid(userId) }))}
                  >
                    Klient w CRM
                  </Button>
                  {userCart && (
                    <Button
                      variant="outline"
                      icon={<MdShoppingCart />}
                      onClick={() => navigate(getMegaraxPath.uri("megarax://loyalty/carts/id/{id}", { id: userCart.id }))}
                    >
                      Koszyk użytkownika
                    </Button>
                  )}
                  <PostHogUserButton userId={userId} />
                </div>
                <div>
                  <div
                    css={{
                      fontSize: "1rem",
                      fontWeight: 600,
                      marginBottom: "1rem",
                    }}
                  >
                    Historia operacji
                  </div>
                  {account.history.length > 0 ? (
                    <AccountHistoryTable history={account.history} />
                  ) : (
                    <div css={{ color: theme.colors.secondaryText || "#666", padding: "1rem" }}>
                      Brak historii operacji
                    </div>
                  )}
                </div>
                <div>
                  <div
                    css={{
                      fontSize: "1rem",
                      fontWeight: 600,
                      marginBottom: "1rem",
                    }}
                  >
                    Historia zamówień
                  </div>
                  {(ordersQuery.data?.count ?? 0) > ordersPageSize && (
                    <div
                      css={{
                        display: "flex",
                        justifyContent: "flex-end",
                        width: "100%",
                        marginBottom: "1rem",
                      }}
                    >
                      <Pagination
                        page={ordersPage}
                        onPageChange={(p) => {
                          setOrdersPage(p);
                          window.scrollTo({ top: 0, behavior: "smooth" });
                        }}
                        pageSize={ordersPageSize}
                        itemCount={ordersQuery.data?.count ?? 0}
                      />
                    </div>
                  )}
                  <QuerySkeleton query={ordersQuery}>
                    {(orders) =>
                      orders.items.length > 0 ? (
                        <div css={{ display: "flex", flexDirection: "column", gap: "0.75rem" }}>
                          {orders.items.map((order) => (
                            <OrderItem key={order.id} order={order} />
                          ))}
                        </div>
                      ) : (
                        <div css={{ color: theme.colors.secondaryText || "#666", padding: "1rem" }}>Brak zamówień</div>
                      )
                    }
                  </QuerySkeleton>
                </div>
              </div>
            </Page>
          </>
        );
      }}
    </QuerySkeleton>
  );
};
